/* eslint-disable @typescript-eslint/ban-ts-comment */
import { FC, useEffect, useRef, useState } from 'react';
import { useNavigate, useHref, useLinkClickHandler } from 'react-router';
import {
  Button,
  ButtonWC,
  Icon,
  Dropdown,
  DropdownWC,
  Stack,
  Typeahead,
  Divider,
  Spacer,
  Menu,
  MenuItem,
  TextSubtitle,
  TypeaheadWC,
} from '@pypestream/design-system';

import {
  ProStudioPages,
  useCurrentRoute,
  useProStudioRoutes,
} from '../../hooks';
import {
  sendProStudioEvent,
  useProStudioCtxSelector,
} from '../../xstate/app.xstate';
import {
  computeActiveSolution,
  computeScrollTopForItem,
} from './solutions-dropdown.utils';
import { buildSolutionHref, isPlainLeftClick } from '../../utils/solution-link';
import { useTranslation } from '@pypestream/translations';

interface SolutionItem {
  id?: string;
  name?: string;
}

interface SolutionMenuItemLinkProps {
  item: SolutionItem;
  projectId: string;
  activeSolutionId?: string;
  onSelect: (item: SolutionItem) => void;
  onClose: () => void;
}

const SolutionMenuItemLink: FC<SolutionMenuItemLinkProps> = ({
  item,
  projectId,
  activeSolutionId,
  onSelect,
  onClose,
}) => {
  const href = useHref(buildSolutionHref(projectId, item.id ?? ''));
  return (
    // MenuItem renders its label into shadow DOM; the light-DOM <a> is the navigation vehicle and
    // MUST generate a real box (display:block) so it is the pointer hit-test target — the browser
    // resolves middle-click (new tab) and right-click ("Open link in new tab") from the nearest
    // <a> BOX under the cursor. An earlier display:contents removed that box, so the hit landed on
    // the ps-menu-item and the browser saw no link → right/middle-click silently did nothing while
    // cmd+click (a JS click handler, not the native box path) still worked (PSTUDIO-2541). block +
    // inherit keeps the row's look; ps-menu-item is itself display:block so layout is unchanged.
    <a
      href={href}
      role="link"
      style={{ display: 'block', color: 'inherit', textDecoration: 'none' }}
      onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
        if (e.defaultPrevented) return;
        if (!isPlainLeftClick(e.nativeEvent)) {
          // cmd/ctrl+click → the browser opens href in a new tab; close the dropdown so it does not
          // linger open on the original tab (cmd+click keeps focus here on macOS → no focusout).
          onClose();
          return;
        }
        e.preventDefault(); // plain left-click → run the existing SPA flow
        onSelect(item);
      }}
      onAuxClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
        // Middle (scroll-wheel) click fires auxclick, not click: the native <a href> opens the new
        // tab on its own; mirror the cmd+click branch and close the dropdown so it does not linger.
        if (e.button === 1) onClose();
      }}
    >
      <MenuItem value={item.id} selected={item.id === activeSolutionId}>
        {item.name}
      </MenuItem>
    </a>
  );
};

export const SolutionsDropdown: FC = () => {
  const { t } = useTranslation();
  const {
    solutions,
    projectId,
    selectedSolution,
    hasSolutionScope,
    isSwitchingSolution,
  } = useProStudioCtxSelector((ctx) => ({
    projectId: ctx.project?.id,
    selectedSolution: ctx.selectedBot,
    /**
     * Does the CURRENT URL name a solution? Both fields are route-derived (see
     * `syncXstateWithRouteParams`), and the project-level routes clear them via
     * `clearSolutionScope` — so this going false is the signal that the
     * breadcrumb must fall back to "All Solutions", the way a reload of the same
     * URL already did.
     */
    hasSolutionScope: !!ctx.botName || !!ctx.botIdURLParam,
    isSwitchingSolution: !!ctx.isSwitchingSolution,
    solutions: (ctx.solutions || [])
      .map((bot) => ({
        id: bot.id,
        name: bot.displayName,
      }))
      // @todo - move this sorting to gql query, currently we don't have ability to add sorting to the getSolutions query so doing it here
      .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
  }));

  const [activeSolution, setActiveSolution] = useState<SolutionItem | null>();

  const [filteredOptions, setFilteredOptions] =
    useState<SolutionItem[]>(solutions);
  const [value, setValue] = useState<string>('');

  const dropdownRef = useRef<DropdownWC | null>(null);

  const navigate = useNavigate();
  const routes = useProStudioRoutes();

  const currentRoute = useCurrentRoute();
  const isHomePage = currentRoute.label === routes.home.label;

  const homepageHref = useHref(routes.home.path);
  const handleHomepageClick = useLinkClickHandler<ButtonWC>(routes.home.path);

  const handleTypeheadInput = (event: CustomEvent) => {
    setFilteredOptions(event.detail.filtered);
    setValue(event.detail.value);
  };

  useEffect(() => {
    if (!solutions) return;
    setFilteredOptions(solutions);
  }, [solutions]);

  // Keep the breadcrumb's active solution in sync with the selected solution.
  // The guard compares id AND name so a rename of the current solution (same
  // uniqueId, new displayName) reflects live, without a reload (PSTUDIO-3596).
  useEffect(() => {
    // Navigated to a route that names no solution (Project Settings, the
    // Solutions list, project configurations): show "All Solutions". Clearing
    // `ctx.selectedBot` alone wouldn't do it — `computeActiveSolution` treats a
    // missing selection as "nothing to say" and leaves this state untouched, so
    // the breadcrumb kept the name of the solution the user came from.
    //
    // Skipped mid-switch: `selectBot` empties the bot-scoped context BEFORE the
    // new solution's route sync lands, and the click handler has already set the
    // picked solution optimistically — resetting here would flash "All Solutions"
    // over it on every switch.
    if (!hasSolutionScope && !isSwitchingSolution) {
      if (activeSolution) setActiveSolution(null);
      return;
    }

    const { shouldUpdate, next } = computeActiveSolution({
      selectedSolution,
      activeSolution,
      isHomePage,
    });
    if (shouldUpdate) setActiveSolution(next);
  }, [
    selectedSolution,
    activeSolution,
    isHomePage,
    hasSolutionScope,
    isSwitchingSolution,
  ]);

  // When the dropdown opens, bring the active solution into view inside the
  // scrollable list — long solution lists otherwise open scrolled to the top,
  // hiding the current selection. `ps-dropdown` extends the popover base, which
  // emits `popover-open` on show; rAF waits for the panel to lay out before we
  // measure. Only the list container scrolls (never the page or the popover).
  useEffect(() => {
    const dropdown = dropdownRef.current;
    if (!dropdown) return undefined;

    const handleOpen = () => {
      requestAnimationFrame(() => {
        const root = dropdownRef.current;
        if (!root) return;
        // PSTUDIO-3849: focus the search input as soon as the dropdown paints so
        // the user can type immediately. Must run BEFORE the scroll early-returns
        // below, which bail when there is no selected item (home / "All Solutions").
        root.querySelector<TypeaheadWC>('ps-typeahead')?.focus();
        const item = root.querySelector<HTMLElement>('ps-menu-item[selected]');
        const container = root.querySelector<HTMLElement>(
          '.c-primary-nav__solutions-list'
        );
        if (!item || !container) return;

        const itemRect = item.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        container.scrollTop = computeScrollTopForItem({
          itemTop: itemRect.top,
          itemBottom: itemRect.bottom,
          containerTop: containerRect.top,
          containerBottom: containerRect.bottom,
          scrollTop: container.scrollTop,
        });
      });
    };

    dropdown.addEventListener('popover-open', handleOpen);
    return () => dropdown.removeEventListener('popover-open', handleOpen);
    // Re-run once `projectId` turns truthy: the dropdown is only rendered past
    // the `if (!projectId) return` guard below, so the ref isn't populated on
    // the initial (projectId-less) render.
  }, [projectId]);

  if (!projectId) return;

  const allSolutionsText = t('pro-studio/navigation:vocabulary.all_solutions');

  return (
    <>
      <Button
        test-id="ps-solutions-dropdown-trigger"
        id="solutions-selector-trigger"
        slot="nav-breadcrumb-extra"
        variant="ghost"
        size="xlarge"
        className="c-primary-nav__solutions-button"
        title={activeSolution?.name || undefined}
      >
        <span className="c-primary-nav__solutions-button-text">
          {isHomePage
            ? allSolutionsText
            : activeSolution?.name || allSolutionsText}
        </span>
        <Icon name="chevrons-up-down" size="xsmall" slot="suffix" />
      </Button>
      <Dropdown
        // @ts-ignore
        ref={dropdownRef}
        trigger="solutions-selector-trigger"
        placement="top-start"
        test-id="ps-solutions-dropdown"
      >
        <div className="c-primary-nav__solutions-dropdown">
          <div className="c-primary-nav__solutions-search">
            <Stack gutter="2xsmall" nowrap>
              <Typeahead
                size="medium"
                searchKeys={['name']}
                value={value}
                data={solutions as SolutionItem[]}
                onInput={handleTypeheadInput}
              />
              <Button
                test-id="nav-link-solutions"
                variant="validation-neutral"
                href={homepageHref}
                onClick={(event: React.MouseEvent<ButtonWC, MouseEvent>) => {
                  setActiveSolution(null);
                  dropdownRef.current?.hide();
                  handleHomepageClick(event);
                }}
                style={{ flexShrink: 0 }}
              >
                {allSolutionsText}
              </Button>
            </Stack>
            <Spacer size="2xsmall" />
            <Divider />
          </div>
          <div className="c-primary-nav__solutions-list">
            <Spacer size="2xsmall" />
            <Menu>
              {filteredOptions?.map((item) =>
                item.id ? (
                  <SolutionMenuItemLink
                    key={item.id}
                    item={item}
                    projectId={projectId}
                    activeSolutionId={activeSolution?.id}
                    onClose={() => dropdownRef.current?.hide()}
                    onSelect={(selected) => {
                      if (!selected.id || !selected.name) return;
                      setActiveSolution(selected);
                      sendProStudioEvent({
                        type: 'selectBot',
                        name: selected.name,
                        id: selected.id,
                        cb: (path?: string) => {
                          const currentPage =
                            currentRoute.route ===
                              ProStudioPages.CHANNELS.toLowerCase() ||
                            currentRoute.route ===
                              ProStudioPages.FILES.toLowerCase() ||
                            currentRoute.route ===
                              ProStudioPages.WORKFLOWS.toLowerCase() ||
                            currentRoute.route ===
                              ProStudioPages.ACTIONS.toLowerCase()
                              ? `/${currentRoute.route}`
                              : '';

                          const newPath = `${path}${currentPage}`;
                          navigate(newPath || '');
                        },
                      });
                    }}
                  />
                ) : null
              )}
            </Menu>
            {!filteredOptions?.length && (
              <>
                <Spacer size="xsmall" />
                <TextSubtitle
                  size="xsmall"
                  variant="tertiary"
                  textAlign="center"
                >
                  No solutions found
                </TextSubtitle>
              </>
            )}
          </div>
        </div>
      </Dropdown>
    </>
  );
};
